<script lang="ts" setup>
  import type { ID } from '@/types'

  import { watch, nextTick } from 'vue'
  import { watchDebounced } from '@vueuse/core'

  import { useRouter } from 'vue-router'
  import { useRouteQuery } from '@vueuse/router'

  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui'
  import { useCustomerAuthStore } from '@/stores/customer/auth'

  import { formatPrice } from '@/helpers'

  import { useCartStarter } from '@/composables/useCartStarter'
  import { useCustomerAuthSettings } from '@/composables/useCustomerAuthSettings'

  import ManagerCurtain from '@/components/Modals/Manager/ManagerCurtain.vue'
  import CustomerCartProductList from '@/components/Global/Customer/Cart/CustomerCartProductList.vue'
  import ManagerButton from '@/components/Global/Manager/Forms/ManagerButton.vue'

  const ui = useUiStore()
  const { cartVisibility } = storeToRefs(ui)

  const {
    cartStore,

    products,
    cartItems,
    cartItemsAmount,

    totalSum,
    discountedSum,
    personalDiscountSum,
    couponDiscountSum,
    finalDiscountSum,
    finalDiscountEntity,

    sendCart,
    reset,
    fullReset,
  } = useCartStarter()

  const router = useRouter()
  const cartQuery = useRouteQuery('cart')

  watch(
    () => [cartQuery.value, cartVisibility.value],
    (newValues, oldValues) => {
      const [newCartQuery, newCartVisibility] = newValues ?? [undefined, false]
      const [oldCartQuery, oldCartVisibility] = oldValues ?? [undefined, false]

      if (newCartQuery) {
        if (!oldCartQuery) cartVisibility.value = true
        else if (!newCartVisibility && oldCartVisibility) {
          router.replace({ query: undefined })
        }
      } else if (oldCartQuery) cartVisibility.value = false
    },
    { immediate: true }
  )

  watch(
    () => cartVisibility.value,
    async () => {
      if (cartVisibility.value) {
        fullReset()
        await sendCart()
      } else reset()
    },
    { immediate: true }
  )
  watchDebounced(
    () => cartItemsAmount.value,
    async () => {
      if (!cartVisibility.value) {
        fullReset()

        return
      }

      await sendCart()
    },
    { debounce: 2000 }
  )

  const customerAuthSettings = useCustomerAuthSettings()

  const customerAuthStore = useCustomerAuthStore()
  const { authorized: customerAuthorized } = storeToRefs(customerAuthStore)

  async function apply() {
    if (!cartItems.value.length) return

    const skip =
      customerAuthorized.value || (await customerAuthSettings.checkSettings())

    if (skip) await router.push({ name: 'customer-checkout' })
    else {
      cartVisibility.value = false

      await nextTick()

      ui.showCurtainLogin(true)
    }
  }

  function changeAmount({ id, amount }: { id: ID; amount?: number }) {
    if (!cartStore.canAdd(id)) return

    cartStore.handleProduct(id, amount)
  }
</script>

<template>
  <ManagerCurtain
    v-model:active="cartVisibility"
    close-btn-classes="top-4 right-5"
    curtain-classes="w-full max-w-[360px] lg:max-w-[640px]"
    :show-shadow="false"
  >
    <!-- Curtain Header -->
    <div class="curtain-header py-4 pl-6 pr-5 h-16 lg:h-28">
      <span class="relative flex items-start">
        <span class="text-mblack1 text-c2sm lg:text-cbase uppercase font-bold">
          Корзина</span
        ><span
          v-if="cartItemsAmount"
          class="text-c2.5xs lg:text-cxs text-mgray2 align-text-top ml-2"
        >
          {{ cartItemsAmount }}
        </span>
      </span>
    </div>

    <template #close-btn-icon>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M4 20L20 4M20 20L4 4"
          stroke="#1F1F1F"
          stroke-width="1.2"
          stroke-linecap="square"
        />
      </svg>
    </template>

    <!-- Curtain Body -->
    <div
      class="curtain-contents border-y border-mbrd max-h-full overflow-y-auto overflow-x-hidden px-6 text-cxs lg:text-csm"
    >
      <CustomerCartProductList
        v-if="cartItems.length && products.length"
        editable
        :items="products"
        :cart-items="cartItems"
        @change-amount="changeAmount"
      />

      <div
        v-else-if="!cartItems.length"
        class="pt-6 text-cxs lg:text-csm text-mgray"
      >
        Ваша корзина пуста
      </div>
    </div>

    <!-- Curtain Footer -->
    <div v-if="cartItems.length && products.length">
      <div
        class="flex flex-col justify-end py-4 px-6 border-t border-mbrd min-h-[139px] lg:min-h-[161px]"
      >
        <div
          v-if="finalDiscountEntity"
          class="flex items-center h-[14px] lg:h-[17px] mb-1 text-c2.5xs lg:text-cxs text-mgray2"
        >
          <div class="grow">Сумма</div>

          <div class="text-right grow">
            {{ formatPrice(totalSum) }}&nbsp;&#8381;
          </div>
        </div>

        <div
          v-if="finalDiscountEntity"
          class="flex items-center h-[14px] lg:h-[17px] mb-1 text-c2.5xs lg:text-cxs text-mgray2"
        >
          <div v-if="personalDiscountSum === finalDiscountSum" class="grow">
            Скидка по системе лояльности {{ finalDiscountEntity?.amount ?? 0 }}%
          </div>

          <div v-else-if="couponDiscountSum === finalDiscountSum" class="grow">
            Скидка по промокоду {{ finalDiscountEntity?.amount ?? 0
            }}<template v-if="!finalDiscountEntity?.absolute">%</template>
            <template v-else>&#8381;</template>
          </div>

          <div class="text-right grow">
            {{ formatPrice(finalDiscountSum) }}&nbsp;&#8381;
          </div>
        </div>

        <div class="flex items-center mb-1 py-1 text-cbase lg:text-c3base">
          <div class="w-1/2">Итого</div>

          <div class="w-1/2 text-right">
            {{ formatPrice(discountedSum) }}&nbsp;&#8381;
          </div>
        </div>

        <ManagerButton
          caption="Оформить заказ"
          class="rounded-none !h-9 lg:!h-12 !text-cxs lg:!text-csm"
          :disabled="!cartItems.length"
          @click="apply"
        />
      </div>
    </div>
  </ManagerCurtain>
</template>

<style scoped>
  .curtain-contents {
    height: calc(100% - 64px - 210px);
  }

  @media screen and (min-width: theme('screens.lg')) {
    .curtain-contents {
      height: calc(100% - 112px - 241px);
    }
  }
</style>
